"use client";

// The mode rail (design spec §3): 56 px wide, the brand mark on top, one 48×48 item per mode
// (20 px icon over a 10 px label), Setup and the operator console pinned at the bottom. The
// registry decides which items exist; the rail only draws them.
import { LockIcon, type LucideIcon } from "lucide-react";
import type * as React from "react";
import { cn } from "../cn";
import { CountPill } from "./count-pill";
import { FOCUS_RING } from "./focus-ring";
import { Kbd, KbdGroup } from "./kbd";
import { Tooltip, TooltipContent, TooltipTrigger } from "./tooltip";

function Rail({
  brand,
  bottom,
  children,
  className,
  "aria-label": label = "Modes",
}: {
  brand?: React.ReactNode;
  /** Items pinned to the bottom (Setup, Operator). */
  bottom?: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
  "aria-label"?: string;
}) {
  return (
    <nav
      aria-label={label}
      data-slot="rail"
      className={cn(
        // A short window scrolls the rail (no scrollbar shown) rather than clip Settings and Me.
        "flex h-full min-h-0 w-rail shrink-0 flex-col items-center overflow-y-auto no-scrollbar border-r border-sidebar-border bg-sidebar pb-2",
        className,
      )}
    >
      {brand ? (
        <div className="flex h-topbar w-full shrink-0 items-center justify-center">{brand}</div>
      ) : null}
      <ul className="flex flex-col items-center gap-1">{children}</ul>
      {bottom ? <ul className="mt-auto flex flex-col items-center gap-1">{bottom}</ul> : null}
    </nav>
  );
}

const ITEM = `relative flex size-12 flex-col items-center justify-center gap-0.5 rounded-md text-secondary transition-colors duration-(--dur-fast) ease-out hover:bg-muted hover:text-foreground aria-[current=page]:bg-primary-soft aria-[current=page]:text-primary-text ${FOCUS_RING}`;

type RailItemProps = {
  "aria-label"?: string | undefined;
  href?: string;
  className?: string;
  /** Extra data attributes (e.g. `data-mode`) for tests and styling hooks. */
  [data: `data-${string}`]: string | undefined;
  icon: LucideIcon;
  label: string;
  /** Marks the item current when the router does not (`aria-current` wins either way). */
  active?: boolean | undefined;
  /** The `g` chord, e.g. `["g", "k"]`, shown in the tooltip. */
  chord?: readonly string[] | undefined;
  /** `locked`: add-on off (owners and admins only); `read-only`: grace period. */
  state?: "locked" | "read-only" | undefined;
  /** Tooltip text; defaults to the label. */
  hint?: string | undefined;
  count?: number | undefined;
  /** A small accent dot (something is live: an agent screen running). */
  dot?: boolean | undefined;
  /** A router link element (`<Link to=… />`); defaults to `<a>`. */
  render?: React.ReactElement;
};

function RailItem({
  icon: Icon,
  label,
  active,
  chord,
  state,
  hint,
  count,
  dot,
  render,
  className,
  ...props
}: RailItemProps) {
  // A plain anchor unless the app passes its router link; the trigger fills in href and content.
  // biome-ignore lint/a11y/useValidAnchor: href and children arrive through the trigger's props.
  // biome-ignore lint/a11y/useAnchorContent: see above.
  const element = render ?? <a />;
  return (
    <li>
      <Tooltip>
        <TooltipTrigger
          render={element}
          data-slot="rail-item"
          data-state={state}
          aria-current={active && state !== "locked" ? "page" : undefined}
          className={cn(
            ITEM,
            // A locked item opens Add-ons: it is never the current page, whatever the router says.
            state === "locked" &&
              "aria-[current=page]:bg-transparent aria-[current=page]:text-secondary",
            className,
          )}
          {...props}
        >
          <span className="relative">
            <Icon aria-hidden="true" className="size-5" />
            {state === "locked" ? (
              <LockIcon
                aria-hidden="true"
                className="absolute -right-1.5 -bottom-1 size-3 rounded-full bg-sidebar p-px text-muted-foreground"
              />
            ) : null}
          </span>
          <span className="max-w-full truncate text-[10px] leading-3 font-medium tracking-[-0.2px]">
            {label}
          </span>
          {state === "read-only" ? (
            <span
              aria-hidden="true"
              className="absolute top-1.5 right-1.5 size-1.5 rounded-full bg-warning"
            />
          ) : null}
          {dot && !(count && count > 0) ? (
            <span
              aria-hidden="true"
              data-slot="rail-item-dot"
              className="absolute top-1.5 right-2 size-2 rounded-full bg-success ring-2 ring-sidebar"
            />
          ) : null}
          {count && count > 0 ? (
            <CountPill count={count} tone="accent" className="absolute top-0.5 right-0.5" />
          ) : null}
        </TooltipTrigger>
        <TooltipContent side="right">
          {hint ?? label}
          {chord ? (
            <KbdGroup>
              {chord.map((k) => (
                <Kbd key={k}>{k.toUpperCase()}</Kbd>
              ))}
            </KbdGroup>
          ) : null}
        </TooltipContent>
      </Tooltip>
    </li>
  );
}

export { Rail, RailItem };
